import { TranslocoService } from '@jsverse/transloco';
import { patchState, signalStore, type, withMethods, withProps, withState } from '@ngrx/signals';

import { HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { addEntity, updateEntity, withEntities } from '@ngrx/signals/entities';
import { Observable } from 'rxjs';
import { FileService } from '@shared/services/file.service';
import { getApiErrorMessage } from '@helpers/api-errors';
import { TransferEntity, TransferState } from './types';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface FileState {}

const initialState: FileState = {};

// Counter for unique transfer ids.
let nextTransferId = 0;

/**
 * Store for managing file transfers.
 *
 * This store manages the state of file transfers, including their progress,
 * state, and any error messages that occur during the transfer process.
 */
export const FileStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withEntities({ entity: type<TransferEntity>(), collection: 'transfers' }),
  withProps(() => ({
    fileService: inject(FileService),
    translateService: inject(TranslocoService),
  })),
  withMethods(store => ({
    async queueFileUpload<T>(
      uploader: Observable<HttpEvent<T>>,
      fileName: string,
    ): Promise<T | undefined> {
      const id = nextTransferId++;
      patchState(
        store,
        addEntity<TransferEntity, 'transfers'>(
          { id, fileName, state: TransferState.PREPARING, direction: 'upload' },
          { collection: 'transfers' },
        ),
      );
      return new Promise<T | undefined>((resolve, reject) => {
        let response: T | undefined;
        let hasFailed = false;
        const sub = store.fileService.trackUpload(uploader).subscribe({
          next: status => {
            if (status.state === TransferState.DONE) {
              response = status.response;
            }
            patchState(
              store,
              updateEntity(
                {
                  id,
                  changes: {
                    state: status.state,
                    progress: status.progress,
                    errorMessage: status.errorMessage,
                  },
                },
                { collection: 'transfers' },
              ),
            );
            if (status.state === TransferState.FAILED) {
              hasFailed = true;
              reject(
                new Error(
                  status.errorMessage ??
                    store.translateService.translate('globals.files.error.uploadFailedFallback'),
                ),
              );
            }
          },
          complete: () => {
            sub.unsubscribe();
            if (!hasFailed) {
              resolve(response);
            }
          },
          error: err => {
            sub.unsubscribe();
            reject(err);
          },
        });
      });
    },
    async queueFileDownload(
      url: string | (() => Promise<string>),
      fileName: string,
    ): Promise<void> {
      const id = nextTransferId++;
      patchState(
        store,
        addEntity<TransferEntity, 'transfers'>(
          {
            id: id,
            fileName: fileName,
            state: TransferState.PREPARING,
            direction: 'download',
          },
          {
            collection: 'transfers',
          },
        ),
      );

      let finalUrl: string;
      try {
        finalUrl = await Promise.resolve(typeof url === 'function' ? url() : url);
      } catch (error) {
        // An HttpErrorResponse is not an Error, so stringifying it gave
        // "[object Object]". Its body carries a localised message.
        const errorMessage =
          (await getApiErrorMessage(error)) ??
          (error instanceof Error
            ? error.message
            : store.translateService.translate('globals.files.error.downloadFailedFallback'));
        patchState(
          store,
          updateEntity(
            { id, changes: { errorMessage, state: TransferState.FAILED } },
            { collection: 'transfers' },
          ),
        );
        throw new Error(errorMessage, { cause: error });
      }

      if (!finalUrl) {
        const errorMessage = 'Failed to resolve download url';
        patchState(
          store,
          updateEntity(
            { id, changes: { errorMessage, state: TransferState.FAILED } },
            { collection: 'transfers' },
          ),
        );
        throw new Error(errorMessage);
      }

      return new Promise<void>((resolve, reject) => {
        let hasFailed = false;
        const sub = store.fileService.downloadFile(finalUrl, fileName).subscribe({
          next: downloadStatus => {
            patchState(
              store,
              updateEntity(
                {
                  id: id,
                  changes: {
                    state: downloadStatus.state,
                    progress: downloadStatus.progress,
                    errorMessage: downloadStatus.errorMessage,
                  },
                },
                { collection: 'transfers' },
              ),
            );
            if (downloadStatus.state === TransferState.FAILED) {
              hasFailed = true;
              reject(new Error(downloadStatus.errorMessage ?? 'Download failed'));
            }
          },
          complete: () => {
            sub.unsubscribe();
            if (!hasFailed) {
              resolve();
            }
          },
          error: err => {
            sub.unsubscribe();
            reject(err);
          },
        });
      });
    },
  })),
);
